import * as React from 'react';
import { useState } from 'react';
import Icon from 'antd/lib/icon';
import injectSheet from 'react-jss';
import Drawer from 'antd/lib/drawer';
import { StylesProps } from 'src/Library/Types';
import { downloadS3Url, getFileExtension } from 'src/utils/utils';
import { commonStyleSheet } from 'src/Library/CommonStyles';
import Loader from './Loader';

const styles = (theme: any) => ({
  main: {
    fontFamily: 'Open Sans',
    letterSpacing: 0,
    '& .ant-drawer-header': {
      padding: 12,
      height: 56,
      position: 'fixed',
      width: '60%',
    },
    '& .ant-drawer-body': {
      padding: 0,
      backgroundColor: '#F1EEE7',
      height: '92%',
      width: '100%',
      marginTop: 56,
    },
    marginBottom: 100,
  },
  noFileParent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '35%',
    marginBottom: '35%',
  },
  noFileText: {
    color: theme.colors.textSecondary,
    fontSize: theme.fontSize.h2,
    fontWeight: 600,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    ...commonStyleSheet.justifySpaceBetween,
    alignItems: 'center',
    fontWeight: 600,
    fontSize: theme.fontSize.h1,
    color: theme.colors.text,
    width: '100%',
    padding: 0,
  },
  imageDiv: {
    padding: 10,
    backgroundColor: '#F1EEE7',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
  },
  title: {
    marginLeft: 16,
    width: 400,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  downloadIcon: {
    fontSize: 30,
    cursor: 'pointer',
    color: theme.colors.textSecondary,
  },
});

interface FileViewerInterface extends StylesProps<ReturnType<typeof styles>> {
  url?: string;
  title?: string;
  fileName?: string;
  noDrawer?: boolean;
  onClose: () => void;
}

const FileViewer: React.FC<FileViewerInterface> = (props) => {
  const { url, title, onClose, classes, fileName, noDrawer } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string>('');

  React.useEffect(() => {
    let active = true;
    let localBlobUrl = '';
    const currentFileType = getFileExtension(fileName || title);

    if (url && currentFileType === 'pdf') {
      setIsLoading(true);
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error('Failed to fetch PDF');
          return res.blob();
        })
        .then(blob => {
          if (!active) return;
          const pdfBlob = new Blob([blob], { type: 'application/pdf' });
          localBlobUrl = URL.createObjectURL(pdfBlob);
          setBlobUrl(localBlobUrl);
        })
        .catch(() => {
          if (active) {
            setBlobUrl('');
            setIsLoading(false);
          }
        });
    } else {
      setBlobUrl('');
    }

    return () => {
      active = false;
      if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
    };
  }, [url, fileName, title]);

  if (!url || !title) {
    return <></>;
  }
  
  const fileType = getFileExtension(fileName || title);
  const isImage = ['jpeg', 'png', 'jpg'].includes(fileType)

  const hideLoader = () => {
    setIsLoading(false);
  };

  const renderImageViewer = () => {
    return (
      <div className={classes.imageDiv}>
        <img src={url} alt={title} onLoad={hideLoader} />
      </div>
    );
  };

  const renderPdfViewer = () => {
    if (!blobUrl) return null;
    return (
      <iframe
        onLoad={hideLoader}
        width={'100%'}
        height={'100%'}
        src={blobUrl}
      />
    );
  };

  const renderDocumentViewer = () => {
    return (
      <iframe
        onLoad={hideLoader}
        width={'100%'}
        height={'100%'}
        className={fileType}
        src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${url}`}
      />
    );
  };

  const renderExcelViewer = () => {
    return (
      <iframe
        onLoad={hideLoader}
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${url}`}
        width="100%"
        height="100%"
      />
    );
  };
  const renderNoPreview = () => {
    return (
      <div style={{ textAlign: 'center', paddingTop: '20px' }}>
          <p>No preview available for this file type.</p>
        </div>
    )
  }

  const renderContent = () => {
    switch (fileType) {
      case 'jpg':
      case 'jpeg':
      case 'png':
        return renderImageViewer();
      case 'xls':
      case 'xlsx':
      case 'doc':
      case 'docx':
        return renderExcelViewer();
        case 'msg':
        case 'eml':
          return renderNoPreview();
      case 'pdf':
        return renderPdfViewer();
      default:
        return renderDocumentViewer();
    }
  };

  const renderHeader = () => {
    return (
      <div className={classes.header}>
        <Icon type="arrow-right" style={{ cursor: 'pointer' }} onClick={onClose} />
        <div className={classes.header}>
          <span className={classes.title}>{title}</span>
            <Icon type="download" className={classes.downloadIcon} onClick={() => {
                downloadS3Url(url, fileName || title,);
            }}/>
        </div>
      </div>
    );
  };

  const renderSpinner = () => {
    return <Loader zIndex={1} top={56} />;
  };

  const renderInner = () => {
    return (
      <div style={{ backgroundColor: '#F1EEE7', height: '100%', width: '100%' }}>
        {isLoading && renderSpinner()}
        {renderContent()}
      </div>
    );
  };

  const renderViwer = () => {
    if (noDrawer) {
      return renderInner();
    } else {
      return <Drawer
      visible
      width="60%"
      closable={false}
      onClose={onClose}
      title={renderHeader()}
      className={classes.main}
    >{renderInner()}</Drawer>
    }
  }
  
  return renderViwer();
};

export default injectSheet(styles)(FileViewer);
